import axios from '@/util/axios';

import getFileNameFromDirectUploadURL from './getFileNameFromDirectUploadURL';

const getLetterTriggerData = (data) => ({
  data: {
    ...data,
    group_ids: data.groups.map(({ id }) => id),
    letter_id: data.letter.id,
  },
});

const transformResponse = [
  ...axios.defaults.transformResponse,
  (data) => (!data.errors ? data.data : data),
];

const config = {
  'create-agenda-item': (data) => ({
    data: { data: { ...data, speakers: data.speakers.map(({ id }) => id) } },
    method: 'post',
    url: `/api/admin/live_videos/${data.live_video_id}/agenda_items`,
  }),
  'create-letter-trigger': ({ videoId, ...data }) => ({
    data: getLetterTriggerData(data),
    method: 'post',
    url: `/api/admin/live_videos/${videoId}/letter_triggers`,
  }),
  'create-live-video': (data) => ({
    data: {
      data: {
        ...data,
        agenda_item: {
          ...data.agenda_item,
          speakers: data.agenda_item.speakers.map((speaker) => speaker.id),
        },
      },
    },
    method: 'post',
    url: '/api/admin/live_videos',
  }),
  'create-on-demand-topic': (data) => ({
    data: { data },
    method: 'post',
    url: '/api/admin/videos/topics',
  }),
  'create-on-demand-video': (data) => {
    const { upload_url: upload, ...rest } = data;

    return {
      data: {
        data: {
          ...rest,
          ...(data.video_source !== 'url' && {
            url: upload,
            video_file_name: getFileNameFromDirectUploadURL(upload),
          }),
        },
      },
      method: 'post',
      url: '/api/admin/videos',
    };
  },
  'create-shortcut': (data) => ({
    method: 'post',
    url: `/api/admin/videos/${data.id}/shortcuts`,
  }),
  'create-topic-locale': (data) => ({
    data: { data },
    method: 'post',
    url: `/api/admin/videos/topics/${data.id}/locales`,
  }),
  'create-video-locale': (data) => {
    const url = data.isInLiveMode
    ? `/api/admin/live_videos/${data.parentLocaleId ?? data.id}/locales`
    : `/api/admin/videos/${data.id}/locales`;
    return {
      data: { data },
      method: 'post',
      url,
    };
  },
  'delete-letter-trigger': ({ id, videoId }) => ({
    method: 'delete',
    url: `/api/admin/live_videos/${videoId}/letter_triggers/${id}`,
  }),
  'edit-agenda-item': (data) => {
    const payload = { ...data };

    payload.speakers = payload.speakers.map((speaker) => speaker.id);

    return {
      data: { data: payload },
      method: 'put',
      url: `/api/admin/live_videos/${data.live_video_id}/agenda_items/${data.id}`,
    };
  },
  'get-agenda-items': (data) => ({
    url: `/api/admin/live_videos/${data.id}/agenda_items`,
  }),
  'get-letters': () => ({
    transformResponse,
    url: '/api/admin/letters',
  }),
  'get-organization-video-permissions': ({ id }) => ({
    transformResponse,
    url: `/api/admin/organization_video_permissions?user_id=${id}`,
  }),
  'get-speakers': (data) => ({
    url: `/api/admin/groups_and_users/users?search_query=${data.searchValue}`,
  }),
  'get-tags': (data) => ({ url: `/taxonomy/tags/search?query=${data.query}` }),
  'get-videos-admin-views': ({ id, page, perPage, versionId }) => ({
    params: { page, per_page: perPage, version_id: versionId },
    url: `/api/admin/videos/${id}/views`,
  }),
  'get-video-compose': (data) => ({
    params: { version_id: data.versionId },
    url: data.isInLiveMode
      ? `/api/admin/live_videos/${data.id}/compose`
      : `/api/admin/videos/${data.id}/compose`,
  }),
  'get-video-compose-list': (data) => ({
    url: `/api/admin/live_videos/${data.id}/agenda_items`,
  }),
  'get-video-properties': (data) => ({
    params: { version_id: data.versionId },
    url: data.isInLiveMode
      ? `/api/admin/live_videos/${data.videoId}/properties`
      : `/api/admin/videos/${data.videoId}/properties`,
  }),
  'get-video-versions': (data) => ({
    params: { version_id: data.versionId },
    url: data.isInLiveMode
      ? `api/admin/live_videos/${data.id}/versions`
      : `api/admin/videos/${data.id}/versions`,
  }),
  'launch-video': ({ id, agendaItemId }) => ({
    url: `/student/videos/live/${id}/launch/${agendaItemId}`,
    headers: {
      Accept: 'application/json',
      'Content-Type': 'application/json',
    },
  }),
  'publish-live-video': (data) => ({
    data: data.payload,
    method: 'post',
    url: `/api/admin/live_videos/${data.id}/publish`,
  }),
  'publish-video': (data) => ({
    data: data.payload,
    method: 'post',
    url: `/api/admin/videos/${data.id}/publish`,
  }),
  'update-letter-trigger': ({ id, videoId, ...data }) => ({
    data: getLetterTriggerData(data),
    method: 'patch',
    url: `/api/admin/live_videos/${videoId}/letter_triggers/${id}`,
  }),
  'update-video-version-status': (data) => ({
    data: { data: { action: data.action } },
    method: 'put',
    url: `/api/admin/${data.isInLiveMode ? 'live_' : ''}videos/${data.id}/version`,
  }),
  'get-videos-topics-tree': (data) => ({
    url: `/api/admin/videos/topics?organization_id=${data.organizationId}`,
  }),
  'version-video': (data) => ({
    data: {
      data: {
        action: data.is_current_published ? 'archive' : 'restore',
        course_id: data.id,
      },
    },
    method: 'put',
    url: data.isInLiveMode
      ? `api/admin/live_videos/${data.videoId}/version`
      : `api/admin/videos/${data.videoId}/version`,
  }),
  'get-videos-admin-tree': ({ orgId }) => ({
    url: `/api/admin/videos/topics?organization_id=${orgId}`,
  }),
  'get-current-tree-content': ({ currentTreeContentPath }) => ({
    url: `/api/admin${currentTreeContentPath}`,
  }),
  'get-topic-properties': ({ id, versionId }) => ({
    url: `/api/admin/videos/topics/${id}${versionId ? `?version_id=${versionId}` : ''}`,
  }),
  'get-video-version': ({ id, type, versionId }) => ({
    params: { version_id: versionId },
    url: `/api/admin/${type}/${id}/detail`,
  }),
  'create-enrollment': ({ id, versionId }) => ({
    method: 'post',
    url: `/api/learner/videos/${id}/enrollments${versionId ? `?version_id=${versionId}` : ''}`,
  }),
  'update-enrollment': ({ id, versionId, isComplete, progress, time }) => ({
    data: {
      data: {
        is_complete: isComplete,
        time: Math.round(time),
        progress,
      },
    },
    method: 'put',
    url: `/api/learner/videos/${id}/enrollments${versionId ? `?version_id=${versionId}` : ''}`,
  }),
  'get-videos-admin-permissions': ({ id, permissionableId, isInLiveMode }) => {
    const permissionableType = isInLiveMode ? 'Course' : 'ContentListItem';
    return {
      url: `/api/admin/permissions?id=${id}&permissionable_id=${permissionableId}&permissionable_type=${permissionableType}`,
    };
  },
  'get-videos-admin-editor-permissions': ({ permissionableId, isInLiveMode }) => {
    const permissionableType = isInLiveMode ? 'Course' : 'ContentListItem';
    return {
      url: `/api/admin/admin_permissions?permissionable_id=${permissionableId}&permissionable_type=${permissionableType}`,
    };
  },
};

export default (name, data) => config[name]?.(data) || {};
